import type { Row } from '@tanstack/react-table';

// Improved path type that handles nested objects and optional fields better
type NestedPropertyPath<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object | undefined
          ? T[K] extends ReadonlyArray<unknown> | undefined
            ? K
            : T[K] extends Date | undefined
              ? K
              : T[K] extends infer U | undefined
                ? U extends object
                  ? K | `${K}.${NestedPropertyPath<NonNullable<U>>}`
                  : K
                : K
          : K
        : never;
    }[keyof T]
  : never;

// Utility type to get the value type at a nested path
type PropertyValue<T, P extends string> = P extends keyof T
  ? T[P]
  : P extends `${infer K}.${infer Rest}`
    ? K extends keyof T
      ? PropertyValue<T[K], Rest>
      : never
    : never;

function getNestedProperty<T, P extends NestedPropertyPath<T>>(
  obj: T,
  path: P
): PropertyValue<T, P> | undefined {
  const keys = path.split('.') as string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = obj;

  for (const key of keys) {
    if (current === null || current === undefined || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }

  return current as PropertyValue<T, P>;
}

/**
 * Creates a type-safe sorting function for table rows
 * @param path - The dot-separated path to the property to sort by
 * @param direction - Sort direction ('asc' | 'desc'), defaults to 'asc'
 * @returns A sorting function compatible with TanStack Table
 */
export function createSortingFunction<T>(
  path: NestedPropertyPath<T>,
  direction: 'asc' | 'desc' = 'asc'
) {
  return (rowA: Row<T>, rowB: Row<T>): number => {
    const valueA = getNestedProperty(rowA.original, path);
    const valueB = getNestedProperty(rowB.original, path);

    // Handle null/undefined values - put them at the end
    if (valueA === null || valueA === undefined) {
      return valueB === null || valueB === undefined ? 0 : 1;
    }
    if (valueB === null || valueB === undefined) {
      return -1;
    }

    // Handle string comparison
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      const result = valueA.localeCompare(valueB);
      return direction === 'desc' ? -result : result;
    }

    // Handle number comparison
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      const result = valueA - valueB;
      return direction === 'desc' ? -result : result;
    }

    // Handle date comparison
    if (valueA instanceof Date && valueB instanceof Date) {
      const result = valueA.getTime() - valueB.getTime();
      return direction === 'desc' ? -result : result;
    }

    // Handle date string comparison
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      const dateA = new Date(valueA);
      const dateB = new Date(valueB);

      // Check if both are valid dates
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        const result = dateA.getTime() - dateB.getTime();
        return direction === 'desc' ? -result : result;
      }
    }

    // Fallback to string comparison
    const result = String(valueA).localeCompare(String(valueB));
    return direction === 'desc' ? -result : result;
  };
}

/**
 * Alphabetical sorting function specifically for strings
 * @param path - The dot-separated path to the string property to sort by
 * @param direction - Sort direction ('asc' | 'desc'), defaults to 'asc'
 * @returns A sorting function for alphabetical sorting
 */
export function createAlphabeticalSortingFunction<T>(
  path: NestedPropertyPath<T>,
  direction: 'asc' | 'desc' = 'asc'
) {
  return (rowA: Row<T>, rowB: Row<T>): number => {
    const valueA = getNestedProperty(rowA.original, path);
    const valueB = getNestedProperty(rowB.original, path);

    // Handle null/undefined values
    if (!valueA && !valueB) return 0;
    if (!valueA) return 1;
    if (!valueB) return -1;

    const stringA = String(valueA).toLowerCase();
    const stringB = String(valueB).toLowerCase();

    const result = stringA.localeCompare(stringB);
    return direction === 'desc' ? -result : result;
  };
}
